import React from 'react';
import { Clock, AlertCircle, Building2, MapPin, ExternalLink, Play, Download, Trash2 } from 'lucide-react';
import { SavedOutput } from '@/api/types';
import { Select } from '@/components/ui/Select';
import { getCardBorderColor, getStatusColor, isNeedsFollowup, handleDownload } from './SavedUtils';

interface SavedListProps {
  outputs: SavedOutput[];
  onOpen: (output: SavedOutput) => void;
  onDelete: (id: string) => void;
  onUpdateOutput: (id: string, updates: Partial<SavedOutput>) => void;
}

export function SavedList({ outputs, onOpen, onDelete, onUpdateOutput }: SavedListProps) {
  return (
    <div className="flex-1 overflow-y-auto min-h-0 pr-1 no-scrollbar">
      <div className="grid grid-cols-1 gap-3">
      {outputs.map((out, index) => {
        const needsFollowup = isNeedsFollowup(out);
        const td = out.meta?.targetDetails;
        const link = out.meta?.jobLink;
        
        return (
        <div
          key={out.id}
          style={{ zIndex: outputs.length - index }}
          className={`flex flex-col lg:flex-row lg:items-center justify-between bg-white dark:bg-slate-900/50 border ${getCardBorderColor(out.status)} rounded-xl p-4 cursor-pointer group transition-all relative overflow-visible`}
          onClick={() => onOpen(out)}
        >
           <div className="flex items-start gap-4">
             <div>
               <div className="flex flex-wrap items-center gap-2 mb-1.5">
                 <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border ${out.type === 'analysis' ? 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700' : 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50'}`}>
                   {out.type.replace('-', ' ')}
                 </span>
                 {out.created && (
                   <span className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-500">
                     <Clock className="w-3 h-3" />
                     {new Date(out.created).toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                   </span>
                 )}
                 {needsFollowup && (
                   <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 rounded">
                     <AlertCircle className="w-3 h-3" /> Needs Followup
                   </span>
                 )}
               </div>
               <div className="text-sm font-medium text-slate-800 dark:text-slate-200 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors flex flex-wrap items-center gap-2">
                 {out.title}
               </div>
               {td && (
                 <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-slate-500 dark:text-slate-400">
                   {td.company && (
                     <span className="flex items-center gap-1">
                       <Building2 className="w-3.5 h-3.5" />
                       {td.company}
                     </span>
                   )}
                   {td.targetLocation && (
                     <span className="flex items-center gap-1">
                       <MapPin className="w-3.5 h-3.5" />
                       {td.targetLocation}
                     </span>
                   )}
                   {link && (
                     <a 
                       href={link} 
                       target="_blank" 
                       rel="noopener noreferrer" 
                       onClick={(e) => e.stopPropagation()}
                       className="flex items-center gap-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
                     >
                       <ExternalLink className="w-3 h-3" />
                       Job Link
                     </a>
                   )}
                 </div>
               )}
             </div>
           </div>
           
           <div className="mt-4 lg:mt-0 flex items-center justify-between lg:justify-end gap-3 w-full lg:w-auto">
             <div className="flex items-center gap-2">
               <div className="w-[150px] relative z-10" onClick={(e) => e.stopPropagation()}>
                    <Select
                      title="Status"
                      className={`!py-1.5 !px-3 font-semibold uppercase tracking-wider !text-[11px] ${getStatusColor(out.status).split(' ').map(c => c.startsWith('bg') || c.startsWith('text') || c.startsWith('border') || c.startsWith('dark') ? '!' + c : c).join(' ')}`}
                      value={out.status || 'To Apply'}
                      options={[
                        { value: 'To Apply', label: 'To Apply' },
                        { value: 'Applied', label: 'Applied' },
                        { value: 'Interviewing', label: 'Interviewing' },
                        { value: 'Offer', label: 'Offer' },
                        { value: 'Rejected', label: 'Rejected' },
                      ]}
                      onChange={(newStatus) => {
                        const updates: Partial<SavedOutput> = { status: newStatus as any };
                        if (newStatus === 'Applied' && out.status !== 'Applied') {
                          updates.meta = { ...out.meta, appliedDate: new Date().toISOString() };
                        }
                        onUpdateOutput(out.id, updates);
                      }}
                    />
                  </div>
             </div>

             <div className="flex items-center gap-2">
               <button className="flex text-[11px] font-semibold text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 uppercase tracking-wider items-center gap-1.5 px-2 py-1.5 rounded transition-colors cursor-pointer">
                 Open <Play className="w-2.5 h-2.5 fill-current" />
               </button>
               <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1"></div>
               {out.data?.markdown && (
                 <button
                   onClick={(e) => handleDownload(e, out)}
                   className="text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 p-1.5 rounded transition-colors cursor-pointer"
                   title="Download as Markdown"
                   aria-label="Download as Markdown"
                 >
                   <Download className="w-4 h-4" />
                 </button>
               )}
               <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(out.id);
                  }}
                  className="text-slate-400 hover:text-red-600 dark:hover:text-red-400 p-1.5 rounded transition-colors cursor-pointer"
                  title="Delete saved item"
                  aria-label="Delete saved item"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
             </div>
           </div>
        </div>
      )})}
      </div>
    </div>
  );
}
